import React from 'react'
import { IconButton, Typography } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WatchLaterIcon from '@mui/icons-material/WatchLater'
import BlockIcon from '@mui/icons-material/Block'
import CreateIcon from '@mui/icons-material/Create'
import { AVAILABILITY_TYPES } from 'services/freelancer'
import Tags from 'components/Tags'
import Tag from 'components/Tag'
import { classList } from 'services/styles'
import { useFragment, graphql } from 'react-relay'
import { AvailabilityBadge_User$key } from '__generated__/AvailabilityBadge_User.graphql'
import { ZodError } from 'zod-validation-error'
import useStyles from './styles'
import { formSchema } from '../AvailabilityEditDialog'

export interface IAvailabilityBadgeProps {
  editable: boolean
  onEditOpen: () => void
  user: AvailabilityBadge_User$key
}

function AvailabilityBadge(props: IAvailabilityBadgeProps) {
  const { classes } = useStyles()
  const { editable, onEditOpen, user: userFragmentRef } = props

  const user = useFragment(graphql`
    fragment AvailabilityBadge_User on User {
      profile {
        availability
        availabilityType
        availableAt
      }
      timezone
    }
  `, userFragmentRef)

  const timezone = user?.timezone
  const availability = user?.profile?.availability
  const availabilityTypes = (user?.profile?.availabilityType || []).map(type => AVAILABILITY_TYPES.find(t => t.value === type)?.label)
  const availabilityTypeText = availabilityTypes.length === 1 ? `(${availabilityTypes[0]})` : ''

  const editButton = editable && (
    <IconButton
      className={classes.editButton}
      onClick={onEditOpen}
      data-cy="profile-availability-edit"
      size="large"
    >
      <CreateIcon />
    </IconButton>
  )

  let timezoneLabel: React.ReactNode
  let errorLabel: React.ReactNode

  if (timezone || editable) {
    timezoneLabel = (
      <Typography variant="body2" className={classes.timezone} data-cy="timezone">
        {timezone || 'No timezone set'}
      </Typography>
    )
  }

  if (editable) {
    let errorMessages: string[] = []

    try {
      formSchema.parse({
        availability: availability,
        availabilityType: user?.profile?.availabilityType || [],
        availableAt: user?.profile?.availableAt,
      })
    } catch (error) {
      const parsedError = error as ZodError
      if (parsedError.issues.length > 0) {
        errorMessages = parsedError.issues.map(issue => issue.message)
      }
    }

    if (errorMessages.length > 0) {
      errorLabel = (
        <div className={classes.error}>
          <Tags>
            {errorMessages.map((errMessage, i) => (
              <Tag key={i}>
                {errMessage}
              </Tag>
            ))}
          </Tags>
        </div>
      )
    }
  }

  if (editable && (!availability || !availabilityTypes.length)) {
    return (
      <div className={classList(classes.container, classes.notAvailable)}>
        <BlockIcon className={classes.availabilityIcon} />

        <div>
          Availability {editButton}
          {timezoneLabel}
          {errorLabel}
        </div>
      </div>
    )
  }

  switch (availability) {
    case 'available_now':
      return (
        <div className={classList(classes.container, classes.availableNow)} data-cy="available-now">
          <CheckCircleIcon className={classes.availabilityIcon} />

          <div>
            Available {availabilityTypeText} {editButton}
            {timezoneLabel}
            {errorLabel}
          </div>
        </div>
      )

    case 'available_soon':
      return (
        <div className={classList(classes.container, classes.availableSoon)} data-cy="available-soon">
          <WatchLaterIcon className={classes.availabilityIcon} />

          <div>
            Available soon {editButton}
            {timezoneLabel}
            {errorLabel}
          </div>
        </div>
      )

    default:
      return (
        <div className={classList(classes.container, classes.notAvailable)} data-cy="not-available">
          <BlockIcon className={classes.availabilityIcon} />

          <div>
            Currently unavailable {editButton}
            {timezoneLabel}
            {errorLabel}
          </div>
        </div>
      )
  }
}

export default React.memo(AvailabilityBadge)
